"use client";

import { bindPublishedCtaClickDelegation, bindPublishedScrollDepthTracking, computeTimeOnPageSeconds, resetPublishedScrollDepthMilestones } from "@multi-tenant-cms/builder/runtime/visitorIntelligencePublicTrack";
import { buildVisitorIntelligenceCookieDocumentValue, createVisitorIntelligenceCookieState, createVisitorIntelligenceId, LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME, LEGACY_VISITOR_INTELLIGENCE_SESSION_COOKIE_NAME, parseVisitorIntelligenceCookieValue, shapeVisitorIntelligenceEventBatch, touchVisitorIntelligenceCookieState, VISITOR_INTELLIGENCE_EVENT_TYPES } from "@multi-tenant-cms/seo-runtime";
import type { CookieConsentCategorySelection, CookieConsentConfig, VisitorIntelligenceAttribution, VisitorIntelligenceConsentCategory, VisitorIntelligenceDeviceSnapshot, VisitorIntelligenceEventType, VisitorIntelligencePageSnapshot, VisitorIntelligenceQueuedEvent, VisitorIntelligenceVisitorCookieState } from "@multi-tenant-cms/types";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { isVisitorIntelligenceBootstrapAllowed, type TenantVisitorIntelligenceConfig, VISITOR_INTELLIGENCE_BOOTSTRAP_EVENT } from "@/lib/visitor-intelligence";
type VisitorIntelligenceBootstrapState = {
  allowed: boolean;
  category: VisitorIntelligenceConsentCategory;
  flush: (options?: {
    keepalive?: boolean;
  }) => void;
  queue: VisitorIntelligenceQueuedEvent[];
  sessionId: string | null;
  track: (eventType: PublicVisitorIntelligenceEventType, payload?: Record<string, unknown>) => void;
  visitorId: string | null;
};
type VisitorIntelligenceWindow = Window & {
  __CMS_VISITOR_INTELLIGENCE__?: VisitorIntelligenceBootstrapState;
};
type Props = {
  config: TenantVisitorIntelligenceConfig;
  consentConfig: CookieConsentConfig;
  selection: CookieConsentCategorySelection;
};
type PublicVisitorIntelligenceEventType = Exclude<VisitorIntelligenceEventType, "identify">;
const MAX_QUEUED_EVENTS = 100;
const TRACKER_VERSION = "region-runtime-1";
const EVENT_TYPES = new Set<PublicVisitorIntelligenceEventType>(VISITOR_INTELLIGENCE_EVENT_TYPES.filter((eventType): eventType is PublicVisitorIntelligenceEventType => eventType !== "identify"));
function trimNullable(value: string | null | undefined, maxLength: number) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.slice(0, maxLength);
}
function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const encodedName = `${encodeURIComponent(name)}=`;
  for (const chunk of document.cookie.split(";")) {
    const trimmed = chunk.trim();
    if (trimmed.startsWith(encodedName)) {
      return trimmed.slice(encodedName.length);
    }
  }
  return null;
}
function expireCookie(name: string) {
  if (typeof document === "undefined") {
    return;
  }
  const parts = [`${encodeURIComponent(name)}=`, "Max-Age=0", "Path=/", "SameSite=Lax"];
  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }
  // biome-ignore lint/suspicious/noDocumentCookie: The public tracker must clear its first-party cookie immediately when consent changes.
  document.cookie = parts.join("; ");
}
function writeCookieState(config: TenantVisitorIntelligenceConfig, state: VisitorIntelligenceVisitorCookieState) {
  if (typeof document === "undefined") {
    return;
  }
  // biome-ignore lint/suspicious/noDocumentCookie: The public tracker uses a first-party cookie for visitor and session continuity.
  document.cookie = buildVisitorIntelligenceCookieDocumentValue(config, state, {
    secure: window.location.protocol === "https:"
  });
  expireLegacyVisitorCookies(config);
}
function expireLegacyVisitorCookies(config: TenantVisitorIntelligenceConfig) {
  if (config.cookie_name !== LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME) {
    expireCookie(LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME);
  }
  if (config.session_cookie_name !== LEGACY_VISITOR_INTELLIGENCE_SESSION_COOKIE_NAME) {
    expireCookie(LEGACY_VISITOR_INTELLIGENCE_SESSION_COOKIE_NAME);
  }
}
function resolveCookieState(config: TenantVisitorIntelligenceConfig, now = new Date()) {
  const current = parseVisitorIntelligenceCookieValue(readCookieValue(config.cookie_name)) ?? parseVisitorIntelligenceCookieValue(readCookieValue(LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME));
  if (!(current && current.cookie_version === config.cookie_version)) {
    return createVisitorIntelligenceCookieState(config, now);
  }
  const touched = touchVisitorIntelligenceCookieState(current, now.toISOString());
  const lastSeenAt = Date.parse(current.last_seen_at);
  const sessionExpired = Number.isNaN(lastSeenAt) || now.getTime() - lastSeenAt > config.session_timeout_minutes * 60 * 1000;
  if (!sessionExpired) {
    return touched;
  }
  return {
    ...touched,
    session_id: createVisitorIntelligenceId("session"),
    session_started_at: now.toISOString()
  };
}
function sanitizeEventData(value: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!value) {
    return {};
  }
  const sanitized: Record<string, unknown> = {};
  for (const [key, recordValue] of Object.entries(value)) {
    if (typeof key !== "string" || key.trim().length === 0) {
      continue;
    }
    if (recordValue == null || typeof recordValue === "string" || typeof recordValue === "number" || typeof recordValue === "boolean") {
      sanitized[key] = recordValue;
    }
  }
  return sanitized;
}
function resolveDeviceType(userAgent: string): VisitorIntelligenceDeviceSnapshot["device_type"] {
  const normalized = userAgent.toLowerCase();
  if (/bot|crawler|spider|bingpreview|headless|preview|slurp/.test(normalized)) {
    return "bot";
  }
  if (/tablet|ipad/.test(normalized)) {
    return "tablet";
  }
  if (/mobi|android|iphone|ipod/.test(normalized)) {
    return "mobile";
  }
  return "desktop";
}
function resolveBrowser(userAgent: string): string | null {
  if (!userAgent) {
    return null;
  }
  if (/edg\//i.test(userAgent)) {
    return "Edge";
  }
  if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) {
    return "Opera";
  }
  if (/chrome\//i.test(userAgent) && !/edg\//i.test(userAgent)) {
    return "Chrome";
  }
  if (/firefox\//i.test(userAgent)) {
    return "Firefox";
  }
  if (/safari\//i.test(userAgent) && !/chrome\//i.test(userAgent)) {
    return "Safari";
  }
  return null;
}
function resolveOperatingSystem(userAgent: string): string | null {
  if (!userAgent) {
    return null;
  }
  if (/windows nt/i.test(userAgent)) {
    return "Windows";
  }
  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/(iphone|ipad|ipod|ios)/i.test(userAgent)) {
    return "iOS";
  }
  if (/mac os x|macintosh/i.test(userAgent)) {
    return "macOS";
  }
  if (/linux/i.test(userAgent)) {
    return "Linux";
  }
  return null;
}
function buildPageSnapshot(): VisitorIntelligencePageSnapshot {
  const url = new URL(window.location.href);
  url.hash = "";
  url.search = "";
  return {
    path: url.pathname || "/",
    title: trimNullable(document.title, 300),
    url: url.toString()
  };
}
function buildAttribution(pagePath: string): VisitorIntelligenceAttribution {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  let referrerHost: string | null = null;
  let referrerUrl: string | null = null;
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      referrer.hash = "";
      referrer.search = "";
      referrerHost = trimNullable(referrer.host, 255);
      referrerUrl = referrer.toString();
    } catch {
      referrerHost = null;
      referrerUrl = null;
    }
  }
  return {
    campaign: trimNullable(params.get("utm_campaign"), 255),
    content: trimNullable(params.get("utm_content"), 255),
    fbclid: trimNullable(params.get("fbclid"), 255),
    gclid: trimNullable(params.get("gclid"), 255),
    landing_path: pagePath,
    medium: trimNullable(params.get("utm_medium"), 255),
    referrer_host: referrerHost,
    referrer_url: referrerUrl,
    source: trimNullable(params.get("utm_source"), 255) ?? referrerHost,
    term: trimNullable(params.get("utm_term"), 255)
  };
}
function buildDeviceSnapshot(): VisitorIntelligenceDeviceSnapshot {
  const userAgent = navigator.userAgent;
  return {
    browser: trimNullable(resolveBrowser(userAgent), 120),
    device_type: resolveDeviceType(userAgent),
    language: trimNullable(navigator.language, 32),
    operating_system: trimNullable(resolveOperatingSystem(userAgent), 120),
    user_agent: null,
    viewport_height: window.innerHeight > 0 ? Math.trunc(window.innerHeight) : null,
    viewport_width: window.innerWidth > 0 ? Math.trunc(window.innerWidth) : null
  };
}
function createBootstrapState(category: VisitorIntelligenceConsentCategory): VisitorIntelligenceBootstrapState {
  return {
    allowed: true,
    category,
    flush: () => undefined,
    queue: [],
    sessionId: null,
    track: () => undefined,
    visitorId: null
  };
}
export function VisitorIntelligenceBootstrap({
  config,
  consentConfig,
  selection
}: Props) {
  const pathname = usePathname();
  const allowed = isVisitorIntelligenceBootstrapAllowed(config, consentConfig, selection);
  const allowedRef = useRef(allowed);
  const configRef = useRef(config);
  const cookieStateRef = useRef<VisitorIntelligenceVisitorCookieState | null>(null);
  const flushInFlightRef = useRef(false);
  const flushTimerRef = useRef<number | null>(null);
  const lastTrackedPageKeyRef = useRef<string | null>(null);
  const pageEnteredAtRef = useRef<number | null>(null);
  const timeOnPageSentKeyRef = useRef<string | null>(null);
  const queueRef = useRef<VisitorIntelligenceQueuedEvent[]>([]);
  const syncWindowStateRef = useRef<() => void>(() => undefined);
  const flushQueueRef = useRef<(options?: {
    keepalive?: boolean;
  }) => Promise<void>>(async () => undefined);
  const scheduleFlushRef = useRef<() => void>(() => undefined);
  const trackEventRef = useRef<(eventType: PublicVisitorIntelligenceEventType, payload?: Record<string, unknown>) => void>(() => undefined);
  const emitTimeOnPageRef = useRef<(pageKey: string | null) => void>(() => undefined);
  allowedRef.current = allowed;
  configRef.current = config;
  syncWindowStateRef.current = () => {
    const windowWithBootstrap = window as VisitorIntelligenceWindow;
    const state = windowWithBootstrap.__CMS_VISITOR_INTELLIGENCE__;
    if (!state) {
      return;
    }
    state.allowed = allowedRef.current;
    state.category = configRef.current.consent_category;
    state.queue = queueRef.current;
    state.sessionId = cookieStateRef.current?.session_id ?? null;
    state.visitorId = cookieStateRef.current?.visitor_id ?? null;
  };
  scheduleFlushRef.current = () => {
    if (!allowedRef.current || queueRef.current.length === 0 || flushTimerRef.current != null) {
      return;
    }
    // Cap wait so a single page_view still POSTs within a few seconds (default
    // flush_interval_ms is 5000 — users often check Network before then).
    const delayMs = Math.min(configRef.current.flush_interval_ms, 2500);
    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushQueueRef.current().catch(() => undefined);
    }, delayMs);
  };
  flushQueueRef.current = async options => {
    if (!allowedRef.current || flushInFlightRef.current || queueRef.current.length === 0) {
      return;
    }
    if (flushTimerRef.current != null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const cookieState = cookieStateRef.current ?? resolveCookieState(configRef.current);
    cookieStateRef.current = cookieState;
    writeCookieState(configRef.current, cookieState);
    syncWindowStateRef.current();
    const sendCount = Math.min(queueRef.current.length, configRef.current.batch_size, configRef.current.max_events_per_request);
    const pendingEvents = queueRef.current.slice(0, sendCount);
    const batch = shapeVisitorIntelligenceEventBatch({
      config: configRef.current,
      cookie_state: cookieState,
      events: pendingEvents,
      tracker_version: TRACKER_VERSION
    });
    if (batch.events.length === 0) {
      queueRef.current = queueRef.current.slice(sendCount);
      syncWindowStateRef.current();
      return;
    }
    flushInFlightRef.current = true;
    try {
      // `endpoint_path` is already deployment-prefixed at theme load
      // (e.g. `/us-uk/api/...`).
      const response = await fetch(configRef.current.endpoint_path, {
        body: JSON.stringify(batch),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json"
        },
        keepalive: options?.keepalive ?? false,
        method: "POST"
      });
      if (response.ok) {
        let consentDropped = false;
        try {
          const body = (await response.json()) as {
            dropped?: unknown;
          };
          consentDropped = body.dropped === true;
        } catch {
          // Non-JSON success — treat as stored.
        }
        if (consentDropped) {
          // Server can return 202 + dropped (e.g. consent cookie not visible yet).
          // `response.ok` is still true for 2xx, so do not dequeue; retry shortly.
          window.setTimeout(() => {
            flushQueueRef.current().catch(() => undefined);
          }, 400);
          return;
        }
        queueRef.current = queueRef.current.slice(sendCount);
        syncWindowStateRef.current();
        if (queueRef.current.length > 0) {
          scheduleFlushRef.current();
        }
        return;
      }
    } catch {
      // Best-effort tracker transport: keep the queue and retry on the next flush.
    } finally {
      flushInFlightRef.current = false;
    }
    scheduleFlushRef.current();
  };
  trackEventRef.current = (eventType, payload) => {
    if (!(allowedRef.current && EVENT_TYPES.has(eventType))) {
      return;
    }
    const now = new Date();
    const cookieState_0 = resolveCookieState(configRef.current, now);
    cookieStateRef.current = cookieState_0;
    writeCookieState(configRef.current, cookieState_0);
    const page = buildPageSnapshot();
    const nextEvent: VisitorIntelligenceQueuedEvent = {
      attribution: buildAttribution(page.path),
      data: sanitizeEventData(payload),
      device: buildDeviceSnapshot(),
      event_type: eventType,
      occurred_at: now.toISOString(),
      page,
      session_id: cookieState_0.session_id,
      visitor_id: cookieState_0.visitor_id
    };
    queueRef.current.push(nextEvent);
    if (queueRef.current.length > MAX_QUEUED_EVENTS) {
      queueRef.current = queueRef.current.slice(-MAX_QUEUED_EVENTS);
    }
    syncWindowStateRef.current();
    if (queueRef.current.length >= configRef.current.batch_size) {
      flushQueueRef.current().catch(() => undefined);
      return;
    }
    scheduleFlushRef.current();
  };
  emitTimeOnPageRef.current = pageKey => {
    if (pageKey == null || pageEnteredAtRef.current == null) {
      return;
    }
    if (timeOnPageSentKeyRef.current === pageKey) {
      return;
    }
    const seconds = computeTimeOnPageSeconds(pageEnteredAtRef.current, Date.now());
    timeOnPageSentKeyRef.current = pageKey;
    trackEventRef.current("time_on_page", {
      seconds
    });
  };
  useEffect(() => {
    const detail = {
      allowed,
      category: config.consent_category,
      enabled: config.enabled
    };
    const windowWithBootstrap_0 = window as VisitorIntelligenceWindow;
    if (!allowed) {
      if (flushTimerRef.current != null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      cookieStateRef.current = null;
      lastTrackedPageKeyRef.current = null;
      pageEnteredAtRef.current = null;
      timeOnPageSentKeyRef.current = null;
      queueRef.current = [];
      windowWithBootstrap_0.__CMS_VISITOR_INTELLIGENCE__ = undefined;
      expireCookie(config.cookie_name);
      expireCookie(config.session_cookie_name);
      expireLegacyVisitorCookies(config);
      window.dispatchEvent(new CustomEvent(VISITOR_INTELLIGENCE_BOOTSTRAP_EVENT, {
        detail
      }));
      return;
    }
    const state_0 = windowWithBootstrap_0.__CMS_VISITOR_INTELLIGENCE__ ?? createBootstrapState(config.consent_category);
    const cookieState_1 = resolveCookieState(config);
    state_0.category = config.consent_category;
    state_0.flush = options_0 => {
      flushQueueRef.current(options_0).catch(() => undefined);
    };
    state_0.track = (eventType_0, payload_0) => {
      trackEventRef.current(eventType_0, payload_0);
    };
    state_0.allowed = true;
    state_0.queue = queueRef.current;
    state_0.sessionId = cookieState_1.session_id;
    state_0.visitorId = cookieState_1.visitor_id;
    cookieStateRef.current = cookieState_1;
    writeCookieState(config, cookieState_1);
    windowWithBootstrap_0.__CMS_VISITOR_INTELLIGENCE__ = state_0;
    bindPublishedCtaClickDelegation();
    bindPublishedScrollDepthTracking();
    window.dispatchEvent(new CustomEvent(VISITOR_INTELLIGENCE_BOOTSTRAP_EVENT, {
      detail
    }));
  }, [allowed, config]);
  useEffect(() => {
    if (!(allowed && pathname)) {
      return;
    }
    const pageKey_0 = `${window.location.pathname}${window.location.search}`;
    if (lastTrackedPageKeyRef.current === pageKey_0) {
      return;
    }
    if (lastTrackedPageKeyRef.current != null) {
      emitTimeOnPageRef.current(lastTrackedPageKeyRef.current);
    }
    lastTrackedPageKeyRef.current = pageKey_0;
    pageEnteredAtRef.current = Date.now();
    timeOnPageSentKeyRef.current = null;
    trackEventRef.current("page_view");
    resetPublishedScrollDepthMilestones();
  }, [allowed, pathname]);
  useEffect(() => {
    if (!allowed) {
      return;
    }
    const handlePageHide = () => {
      emitTimeOnPageRef.current(lastTrackedPageKeyRef.current);
      flushQueueRef.current({
        keepalive: true
      }).catch(() => undefined);
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [allowed]);
  return null;
}